import React, { useLayoutEffect, useRef } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import i18n from '../../i18n';
import { ReactComponent as CopyIcon } from '../../static/images/svg/mingcute_copy-fill.svg';
import type { MenuStoreLicenseCodeRow } from '../../models/menu-store.entity';
import { CustomerLoading } from '../../component/loading/loading';
import LoadingOverlayButton from '../../component/button/loading-overlay-button.component';
import {
    copyToClipboard,
    formatMenuStoreLicenseStatus,
} from './menu-store.helpers';
import { MenuStoreCommonDialog } from './menu-store-common-dialog.component';
import { dateFormat } from '../../constants/utils';

type MenuStoreLicenseManageDialogProps = {
    /** 弹窗开关。 */
    open: boolean;
    /** 菜单名称。 */
    menuName: string;
    /** 上传时间文本。 */
    dateLabel: string;
    /** 菜单图样式。 */
    menuImageStyle: React.CSSProperties;
    /** 授权码列表加载中。 */
    loading: boolean;
    /** 接口返回的授权码行。 */
    licenseRows: MenuStoreLicenseCodeRow[];
    /** 关闭弹窗。 */
    onClose: () => void;
    /** 提交生成授权码（`POST .../license-codes`），成功后由外层刷新列表。 */
    onGenerateLicense: () => void;
    /** 生成授权码请求进行中，用于按钮加载态。 */
    generateLicenseSubmitting: boolean;
};

/**
 * 授权码管理弹窗。
 */
export function MenuStoreLicenseManageDialog(props: MenuStoreLicenseManageDialogProps): React.ReactElement | null {
    /** 组件属性。 */
    const { open, menuName, dateLabel, menuImageStyle, loading, licenseRows, onClose, onGenerateLicense, generateLicenseSubmitting } = props;
    /** 列表滚动容器，用于生成成功后滚至底部以露出新行 */
    const listScrollRef = useRef<HTMLDivElement>(null);
    /** 上一渲染帧的「生成中」状态，用于识别生成请求结束 */
    const prevGenerateSubmittingRef = useRef(generateLicenseSubmitting);

    useLayoutEffect(() => {
        if (!open) {
            prevGenerateSubmittingRef.current = generateLicenseSubmitting;
            return;
        }
        const wasSubmitting = prevGenerateSubmittingRef.current;
        prevGenerateSubmittingRef.current = generateLicenseSubmitting;
        /** 由「生成中」变为空闲时，将列表滚到底（新码已追加在末尾） */
        if (wasSubmitting && !generateLicenseSubmitting && !loading && listScrollRef.current) {
            const el = listScrollRef.current;
            el.scrollTop = el.scrollHeight;
        }
    }, [open, generateLicenseSubmitting, loading, licenseRows.length]);

    if (!open) {
        return null;
    }
    return (
        <MenuStoreCommonDialog
            title={i18n.t('menu_store_license_manage_title')}
            onClose={onClose}
            dialogClassName="menu-store-license-dialog"
            hideTitleIcon
            hideBottomClose
        >
            <Box className="menu-store-dialog__meta-inline">
                <Box className="menu-store-dialog__meta-thumb menu-store-dialog__meta-thumb--small" style={menuImageStyle} />
                <Box className="menu-store-dialog__meta-grid">
                    <Typography variant="body2" className="menu-store-license-dialog__meta-row">
                        <span className="menu-store-license-dialog__meta-label">{i18n.t('menu_store_activate_menu_name_label')}</span>
                        <span className="menu-store-license-dialog__meta-value">{menuName}</span>
                    </Typography>
                    <Typography variant="body2" className="menu-store-license-dialog__meta-row">
                        <span className="menu-store-license-dialog__meta-label">{i18n.t('menu_store_dialog_upload_time')}</span>
                        <span className="menu-store-license-dialog__meta-value">{dateLabel}</span>
                    </Typography>
                </Box>
            </Box>
            {/* 表头固定、列表区固定高度纵向滚动，避免授权码过多撑破弹窗 */}
            <Box className="menu-store-license-dialog__list-module">
                <Box className="menu-store-dialog__license-head">
                    <Typography variant="caption" className="menu-store-license-dialog__head-text">
                        {i18n.t('menu_store_license_col_code')}
                    </Typography>
                    <Typography variant="caption" className="menu-store-license-dialog__head-text">
                        {i18n.t('menu_store_license_col_status')}
                    </Typography>
                    <Typography variant="caption" className="menu-store-license-dialog__head-text menu-store-license-dialog__head-text--action">
                        {i18n.t('menu_store_license_col_action')}
                    </Typography>
                </Box>
                <Box ref={listScrollRef} className="menu-store-license-dialog__list-scroll">
                    {loading ? (
                        <Box
                            className="menu-store-license-dialog__list-loading-host"
                            role="status"
                            aria-live="polite"
                            aria-label={i18n.t('general_text_loading')}
                        >
                            <CustomerLoading />
                        </Box>
                    ) : licenseRows.length === 0 ? (
                        <Box className="menu-store-license-dialog__list-empty">
                            <Typography variant="body2" className="menu-store-license-dialog__meta-row">
                                {i18n.t('general_text_no_or_more_data')}
                            </Typography>
                        </Box>
                    ) : (
                        licenseRows.map((row, idx) => {
                            /**
                             * 「已激活」时在状态下方展示 `MM/DD/YYYY`（`constants/utils` 的 `dateFormat`）。
                             * `activated_at` 为空时：解析层未取到字段、历史数据无落库时间、或 `dateFormat` 判定非法时间。
                             */
                            const activationDateLabel =
                                row.status === 'activated' && row.activated_at
                                    ? dateFormat(row.activated_at, 'MM/DD/YYYY')
                                    : '';

                            return (
                            <Box
                                key={`${row.code}-${idx}`}
                                className={[
                                    'menu-store-dialog__license-row',
                                    row.status === 'activated'
                                        ? 'menu-store-dialog__license-row--activated'
                                        : 'menu-store-dialog__license-row--pending',
                                ].join(' ')}
                            >
                                <Typography variant="body2" className="menu-store-license-dialog__row-text">
                                    {row.code}
                                </Typography>
                                <Box
                                    className={[
                                        'menu-store-license-dialog__status-cell',
                                        row.status === 'activated'
                                            ? 'menu-store-license-dialog__status-cell--activated'
                                            : 'menu-store-license-dialog__status-cell--pending',
                                    ].join(' ')}
                                >
                                    <Typography
                                        variant="body2"
                                        className="menu-store-license-dialog__status-primary"
                                    >
                                        {formatMenuStoreLicenseStatus(row.status)}
                                    </Typography>
                                    {activationDateLabel ? (
                                        <Typography
                                            variant="body2"
                                            className="menu-store-license-dialog__status-date"
                                        >
                                            {activationDateLabel}
                                        </Typography>
                                    ) : null}
                                </Box>
                                <Box className="menu-store-license-dialog__row-actions">
                                    <IconButton
                                        type="button"
                                        className="menu-store-license-dialog__icon-btn"
                                        size="small"
                                        aria-label={i18n.t('menu_store_dialog_copy')}
                                        onClick={() => copyToClipboard(row.code)}
                                    >
                                        <CopyIcon className="menu-store-license-dialog__row-icon" aria-hidden />
                                    </IconButton>
                                </Box>
                            </Box>
                            );
                        })
                    )}
                </Box>
            </Box>
            <LoadingOverlayButton
                className="menu-store-activate-dialog__submit"
                variant="contained"
                disableElevation
                fullWidth
                sx={{ mt: 1 }}
                loading={generateLicenseSubmitting}
                loadingIndicatorSize={22}
                onClick={onGenerateLicense}
            >
                {i18n.t('menu_store_btn_generate_license')}
            </LoadingOverlayButton>
        </MenuStoreCommonDialog>
    );
}
